import { localeStorageManager } from "@config/localstorage";
import type { LocaleStorageManager } from "@config/localstorage/localstorage.manager";
import { LocaleStorageKeys } from "@config/localstorage/localstorage.types";

type DismissedUserIds = Record<string, true>;
type TecPastSessionsOnboardingStorage = Pick<LocaleStorageManager, "get" | "set">;

const isDismissedUserMap = (value: unknown): value is DismissedUserIds => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;

  return Object.values(value).every(dismissed => dismissed === true);
};

const readDismissedUserIds = (storage: TecPastSessionsOnboardingStorage): DismissedUserIds => {
  try {
    const value = storage.get(LocaleStorageKeys.CHS_2858_ONBOARDING_DISMISSALS);

    if (typeof value !== "string") return {};

    const parsed = JSON.parse(value) as unknown;

    return isDismissedUserMap(parsed) ? { ...parsed } : {};
  } catch {
    return {};
  }
};

export const hasDismissedTecPastSessionsOnboarding = (
  userId: string | null | undefined,
  storage: TecPastSessionsOnboardingStorage = localeStorageManager,
): boolean => {
  const normalizedUserId = userId?.trim();

  if (!normalizedUserId) return false;

  return readDismissedUserIds(storage)[normalizedUserId] === true;
};

export const dismissTecPastSessionsOnboardingForUser = (
  userId: string | null | undefined,
  storage: TecPastSessionsOnboardingStorage = localeStorageManager,
): boolean => {
  const normalizedUserId = userId?.trim();

  if (!normalizedUserId) return false;

  const dismissedUserIds = readDismissedUserIds(storage);

  if (dismissedUserIds[normalizedUserId] === true) return true;

  try {
    storage.set(
      LocaleStorageKeys.CHS_2858_ONBOARDING_DISMISSALS,
      JSON.stringify({ ...dismissedUserIds, [normalizedUserId]: true }),
    );
    return true;
  } catch {
    return false;
  }
};
